import type { PageDimensionsMm } from './geometry';
import type { CompositionLegendEntry, CompositionLegendLayer } from './legend';
import { legendSamples, legendEntryHasBacking, type LegendSample } from './legendSamples';
import type { CompositionLegend, CompositionTypography, NormalizedRect } from './model';

export const DEFAULT_LEGEND_INSET_MM = 5;
const POINT_TO_MM = 25.4 / 72;
export type LegendTextMeasurement = (text: string, typography: CompositionTypography) => number;
export const LEGEND_COLUMN_GUTTER_MM = 3;
export type LegendLayoutSettings = Pick<
  CompositionLegend,
  | 'columnCount'
  | 'repeatContinuationHeadings'
  | 'symbolSizePercent'
  | 'bodyTypography'
  | 'heading'
  | 'border'
  | 'paddingTopMm'
  | 'paddingRightMm'
  | 'paddingBottomMm'
  | 'paddingLeftMm'
  | 'groupSpacingMm'
  | 'itemSpacingMm'
>;

export interface LegendPlannedText {
  readonly value: string;
  readonly lines: readonly string[];
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly lineHeight: number;
  readonly typography: CompositionTypography;
}
export interface LegendPlannedEntry {
  readonly entry: CompositionLegendEntry;
  readonly label: LegendPlannedText;
  readonly sample: LegendSample;
  readonly symbol: {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
    readonly scale: number;
  };
  readonly height: number;
}
export interface LegendGroupFragment {
  readonly layer: CompositionLegendLayer;
  readonly continued: boolean;
  readonly heading: LegendPlannedText | null;
  readonly headingState: 'original' | 'continuation' | 'reserved' | 'hidden';
  readonly entries: readonly LegendPlannedEntry[];
}
export interface LegendPhysicalPlan {
  readonly columnCount: 1 | 2 | 3;
  readonly columns: readonly {
    readonly x: number;
    readonly width: number;
    readonly height: number;
    readonly fragments: readonly LegendGroupFragment[];
  }[];
  readonly title: LegendPlannedText | null;
  readonly empty: LegendPlannedText | null;
  readonly requiredHeight: number;
  readonly requiredWidth: number;
  readonly overflow: boolean;
}

const semanticMeasurement: LegendTextMeasurement = (text, type) =>
  [...text].length * type.fontSizePt * POINT_TO_MM * 0.62;

/** Pure millimetre placement. Renderers consume these lines and positions without reflow. */
export function buildLegendLayout(
  legend: LegendLayoutSettings,
  layers: readonly CompositionLegendLayer[],
  widthMm: number,
  heightMm: number,
  measure: LegendTextMeasurement = semanticMeasurement,
): LegendPhysicalPlan {
  const count = legend.columnCount ?? 1;
  const font = legend.bodyTypography.fontSizePt * POINT_TO_MM;
  const border = legend.border.enabled ? legend.border.widthMm : 0;
  const baseMetrics = legendContentMetrics(widthMm, font, border, legend);
  const sampleFactor = (legend.symbolSizePercent ?? 100) / 100;
  const backed = layers.some((layer) => layer.entries.some(legendEntryHasBacking));
  const metrics = {
    ...baseMetrics,
    symbolWidth: baseMetrics.symbolWidth * sampleFactor * (backed ? 1.6 : 1),
    symbolHeight: baseMetrics.symbolHeight * sampleFactor,
  };
  const usableWidth = widthMm - border * 2 - metrics.paddingLeft - metrics.paddingRight;
  const columnWidth = (usableWidth - LEGEND_COLUMN_GUTTER_MM * (count - 1)) / count;
  const text = (
    value: string,
    typography: CompositionTypography,
    x: number,
    y: number,
    width: number,
    lineHeight: number,
  ): LegendPlannedText => ({
    value,
    lines: wrapLegendText(value, Math.max(0.01, width), (value) => measure(value, typography)),
    typography,
    x,
    y,
    width,
    lineHeight,
  });
  const left = border + metrics.paddingLeft;
  const top = border + metrics.paddingTop;
  const title = legend.heading.enabled
    ? text(
        legend.heading.text || 'Legend',
        legend.heading.typography,
        left,
        top,
        usableWidth,
        legend.heading.typography.fontSizePt * POINT_TO_MM * 1.15,
      )
    : null;
  const contentTop =
    top +
    (title ? title.lines.length * title.lineHeight : 0) +
    legendTitleToFirstGroupSpacing(!!title, layers.length, metrics.groupSpacing);
  const availableHeight = Math.max(0, heightMm - contentTop - metrics.paddingBottom - border);
  const bodyHeading = { ...legend.bodyTypography, fontWeight: 'bold' as const };
  const measured = layers
    .filter((layer) => layer.entries.length)
    .map((layer) => {
      const samples = legendSamples(layer);
      const heading = layer.headingEnabled
        ? text(layer.name, bodyHeading, 0, 0, columnWidth, metrics.lineHeight)
        : null;
      const continued = layer.headingEnabled
        ? legend.repeatContinuationHeadings === false
          ? heading
          : text(`${layer.name} (continued)`, bodyHeading, 0, 0, columnWidth, metrics.lineHeight)
        : null;
      const entries = layer.entries.map((entry, index) => {
        const sample = samples[index]!;
        const symbolHeight = (metrics.symbolHeight * sample.height) / 18;
        const label = text(
          entry.label,
          legend.bodyTypography,
          0,
          0,
          columnWidth - metrics.symbolWidth - metrics.symbolGap,
          metrics.lineHeight,
        );
        return {
          entry,
          label,
          sample,
          height: Math.max(symbolHeight, label.lines.length * metrics.lineHeight),
          symbolHeight,
        };
      });
      return { layer, heading, continued, entries };
    });
  const rows = measured.flatMap((group, groupIndex) =>
    group.entries.map((entry, entryIndex) => ({ group, groupIndex, entry, entryIndex })),
  );
  const groupStarts = new Set<number>();
  const groupsEnds: number[] = [0];
  let offset = 0;
  for (const group of measured) {
    groupStarts.add(offset);
    offset += group.entries.length;
    groupsEnds.push(offset);
  }
  const gapBefore = rows.map((row) =>
    row.entryIndex ? metrics.itemSpacing : row.groupIndex ? metrics.groupSpacing : 0,
  );
  const prefix = [0];
  rows.forEach((row, index) =>
    prefix.push(
      prefix[index]! +
        gapBefore[index]! +
        row.entry.height +
        (row.entryIndex === 0 && row.group.heading
          ? row.group.heading.lines.length * metrics.lineHeight + metrics.groupHeadingGap
          : 0),
    ),
  );
  const rangeHeight = (start: number, end: number) => {
    const row = rows[start]!;
    return (
      prefix[end]! -
      prefix[start]! -
      gapBefore[start]! +
      (row.entryIndex && row.group.continued
        ? row.group.continued.lines.length * metrics.lineHeight + metrics.groupHeadingGap
        : 0)
    );
  };
  interface Partition {
    readonly ends: readonly number[];
    readonly height: number;
    readonly splits: number;
  }
  // Up to three columns; prefix sums keep each candidate O(1). Stable iteration breaks ties.
  const partition = (
    boundaries: readonly number[],
    limit: number,
    preferGroups: boolean,
  ): Partition | null => {
    const populated = Math.min(count, boundaries.length - 1);
    if (populated === 1) {
      const height = rangeHeight(0, rows.length);
      return height <= limit + 1e-7 ? { ends: [rows.length], height, splits: 0 } : null;
    }
    let previous: (Partition | undefined)[] = [{ ends: [], height: 0, splits: 0 }];
    for (let column = 1; column <= populated; column++) {
      const current: (Partition | undefined)[] = [];
      for (let end = column; end < boundaries.length; end++) {
        for (let start = column - 1; start < end; start++) {
          const before = previous[start];
          if (!before) continue;
          const height = rangeHeight(boundaries[start]!, boundaries[end]!);
          if (height > limit + 1e-7) continue;
          const candidate = {
            ends: [...before.ends, boundaries[end]!],
            height: Math.max(before.height, height),
            splits: before.splits + (start && !groupStarts.has(boundaries[start]!) ? 1 : 0),
          };
          const best = current[end];
          if (
            !best ||
            (preferGroups && candidate.splits < best.splits) ||
            ((!preferGroups || candidate.splits === best.splits) &&
              candidate.height < best.height - 1e-7)
          )
            current[end] = candidate;
        }
      }
      previous = current;
    }
    return previous[boundaries.length - 1] ?? null;
  };
  let chosen: Partition | null = null;
  if (rows.length) {
    const whole = partition(groupsEnds, Infinity, false)!;
    if (count === 1 || (measured.length >= count && whole.height <= availableHeight + 1e-7))
      chosen = whole;
    else {
      const boundaries = Array.from({ length: rows.length + 1 }, (_, index) => index);
      chosen =
        partition(boundaries, availableHeight, true) ?? partition(boundaries, Infinity, false);
    }
  }
  let start = 0;
  const columns = (chosen?.ends ?? []).map((end, column) => {
    const x = left + column * (columnWidth + LEGEND_COLUMN_GUTTER_MM);
    let y = contentTop;
    const fragments: LegendGroupFragment[] = [];
    for (let index = start; index < end;) {
      const first = rows[index]!;
      if (fragments.length) y += metrics.groupSpacing;
      const continued = first.entryIndex > 0;
      const sourceHeading = continued ? first.group.continued : first.group.heading;
      const heading = sourceHeading ? { ...sourceHeading, x, y } : null;
      if (heading) y += heading.lines.length * heading.lineHeight + metrics.groupHeadingGap;
      const entries: LegendPlannedEntry[] = [];
      while (index < end && rows[index]!.groupIndex === first.groupIndex) {
        const row = rows[index]!.entry;
        if (entries.length) y += metrics.itemSpacing;
        const scale = metrics.symbolHeight / 18;
        entries.push({
          entry: row.entry,
          sample: row.sample,
          height: row.height,
          label: {
            ...row.label,
            x: x + metrics.symbolWidth + metrics.symbolGap,
            y: y + (row.height - row.label.lines.length * metrics.lineHeight) / 2,
          },
          symbol: {
            x,
            y: y + (row.height - row.symbolHeight) / 2,
            width: metrics.symbolWidth,
            height: row.symbolHeight,
            scale,
          },
        });
        y += row.height;
        index++;
      }
      fragments.push({
        layer: first.group.layer,
        continued,
        heading,
        entries,
        headingState: !heading
          ? 'hidden'
          : !continued
            ? 'original'
            : legend.repeatContinuationHeadings === false
              ? 'reserved'
              : 'continuation',
      });
    }
    start = end;
    return { x, width: columnWidth, height: y - contentTop, fragments };
  });
  const empty = !rows.length
    ? text(
        'No visible layers',
        legend.bodyTypography,
        left,
        contentTop,
        usableWidth,
        metrics.lineHeight,
      )
    : null;
  const requiredHeight =
    contentTop +
    (chosen?.height ?? (empty ? empty.lines.length * empty.lineHeight : 0)) +
    metrics.paddingBottom +
    border;
  // Keep enough room for a short ordinary label. One-column legacy narrow wrapping is retained.
  const minimumColumnWidth =
    metrics.symbolWidth + metrics.symbolGap + (count > 1 ? font * 3 : font);
  const requiredWidth =
    border * 2 +
    metrics.paddingLeft +
    metrics.paddingRight +
    count * minimumColumnWidth +
    (count - 1) * LEGEND_COLUMN_GUTTER_MM;
  const texts = [
    title,
    empty,
    ...columns.flatMap((column) =>
      column.fragments.flatMap((fragment) => [
        fragment.headingState === 'reserved' ? null : fragment.heading,
        ...fragment.entries.map((row) => row.label),
      ]),
    ),
  ];
  const textOverflow = texts.some(
    (item) => item && item.lines.some((line) => measure(line, item.typography) > item.width + 0.05),
  );
  return {
    columnCount: count,
    columns,
    title,
    empty,
    requiredHeight,
    requiredWidth,
    overflow: textOverflow || requiredHeight > heightMm + 0.05 || requiredWidth > widthMm + 0.05,
  };
}

export interface LegendContentMetrics {
  readonly paddingTop: number;
  readonly paddingRight: number;
  readonly paddingBottom: number;
  readonly paddingLeft: number;
  readonly groupSpacing: number;
  readonly itemSpacing: number;
  readonly groupHeadingGap: number;
  readonly contentWidth: number;
  readonly symbolWidth: number;
  readonly symbolHeight: number;
  readonly symbolGap: number;
  readonly labelWidth: number;
  readonly lineHeight: number;
}

/** Shared physical box model used by the DOM, Canvas and vector-PDF legend renderers. */
export function legendContentMetrics(
  boundsWidth: number,
  bodyFontSize: number,
  borderWidth = 0,
  legend?: Pick<
    CompositionLegend,
    | 'paddingTopMm'
    | 'paddingRightMm'
    | 'paddingBottomMm'
    | 'paddingLeftMm'
    | 'groupSpacingMm'
    | 'itemSpacingMm'
  >,
  unitsPerMm = 1,
): LegendContentMetrics {
  const paddingTop = legend ? legend.paddingTopMm * unitsPerMm : bodyFontSize * 0.65;
  const paddingRight = legend ? legend.paddingRightMm * unitsPerMm : bodyFontSize * 0.75;
  const paddingBottom = legend ? legend.paddingBottomMm * unitsPerMm : bodyFontSize * 0.65;
  const paddingLeft = legend ? legend.paddingLeftMm * unitsPerMm : bodyFontSize * 0.75;
  const symbolWidth = bodyFontSize * 2.15;
  const symbolHeight = bodyFontSize * 1.25;
  const symbolGap = bodyFontSize * 0.45;
  const contentWidth = Math.max(1, boundsWidth - borderWidth * 2 - paddingLeft - paddingRight);
  return {
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    groupSpacing: legend ? legend.groupSpacingMm * unitsPerMm : bodyFontSize * 0.5,
    itemSpacing: legend ? legend.itemSpacingMm * unitsPerMm : bodyFontSize * 0.25,
    groupHeadingGap: bodyFontSize * 0.35,
    contentWidth,
    symbolWidth,
    symbolHeight,
    symbolGap,
    labelWidth: Math.max(1, contentWidth - symbolWidth - symbolGap),
    lineHeight: bodyFontSize * 1.25,
  };
}

export function effectiveLegendCornerRadius(
  authoredRadius: number,
  width: number,
  height: number,
): number {
  return Math.max(0, Math.min(authoredRadius, width / 2, height / 2));
}

/** Number of authored group-spacing gaps between effective top-level legend blocks. */
export function legendTopLevelGapCount(headingEnabled: boolean, groupCount: number): number {
  if (groupCount <= 0) return 0;
  return Math.max(0, groupCount - 1) + (headingEnabled ? 1 : 0);
}

export function legendTitleToFirstGroupSpacing(
  headingEnabled: boolean,
  groupCount: number,
  groupSpacing: number,
): number {
  return headingEnabled && groupCount > 0 ? groupSpacing : 0;
}

/** Wraps at words and, like browser text, breaks an individually overlong token when necessary. */
export function wrapLegendText(
  text: string,
  availableWidth: number,
  measure: (value: string) => number,
): readonly string[] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [''];
  const lines: string[] = [];
  let line = '';
  const pushToken = (token: string) => {
    if (measure(token) <= availableWidth) {
      line = token;
      return;
    }
    let fragment = '';
    for (const character of token) {
      if (fragment && measure(fragment + character) > availableWidth) {
        lines.push(fragment);
        fragment = character;
      } else {
        fragment += character;
      }
    }
    line = fragment;
  };
  for (const word of words) {
    if (!line) {
      pushToken(word);
      continue;
    }
    const candidate = `${line} ${word}`;
    if (measure(candidate) <= availableWidth) line = candidate;
    else {
      lines.push(line);
      line = '';
      pushToken(word);
    }
  }
  if (line || !lines.length) lines.push(line);
  return lines;
}

/** Async counterpart for PDF font fallback measurement. */
export async function wrapLegendTextAsync(
  text: string,
  availableWidth: number,
  measure: (value: string) => Promise<number>,
): Promise<readonly string[]> {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [''];
  const lines: string[] = [];
  let line = '';
  const pushToken = async (token: string) => {
    if ((await measure(token)) <= availableWidth) {
      line = token;
      return;
    }
    let fragment = '';
    for (const character of token) {
      if (fragment && (await measure(fragment + character)) > availableWidth) {
        lines.push(fragment);
        fragment = character;
      } else {
        fragment += character;
      }
    }
    line = fragment;
  };
  for (const word of words) {
    if (!line) {
      await pushToken(word);
      continue;
    }
    const candidate = `${line} ${word}`;
    if ((await measure(candidate)) <= availableWidth) line = candidate;
    else {
      lines.push(line);
      line = '';
      await pushToken(word);
    }
  }
  if (line || !lines.length) lines.push(line);
  return lines;
}

/** Deterministic authored fit based on semantic content, never browser pixels. */
export function fittedLegendBounds(
  legend: CompositionLegend,
  layers: readonly CompositionLegendLayer[],
  mapFrame: NormalizedRect,
  page: PageDimensionsMm,
  anchor?: { readonly right: number; readonly bottom: number; readonly top?: number },
  measure?: LegendTextMeasurement,
): NormalizedRect {
  const bodyMm = legend.bodyTypography.fontSizePt * POINT_TO_MM;
  const headingMm = legend.heading.typography.fontSizePt * POINT_TO_MM;
  const entryLongest = Math.max(
    8,
    ...layers.flatMap((layer) => layer.entries.map((entry) => [...entry.label].length)),
  );
  const fullWidthLongestMm = Math.max(
    0,
    ...layers.flatMap((layer) => (layer.headingEnabled ? [[...layer.name].length * bodyMm] : [])),
    ...(legend.heading.enabled ? [[...(legend.heading.text || 'Legend')].length * headingMm] : []),
  );
  const horizontalInset =
    legend.paddingLeftMm +
    legend.paddingRightMm +
    (legend.border.enabled ? legend.border.widthMm * 2 : 0);
  // Keep authored bounds valid even when the frame has no usable furniture area.
  // The resulting physical plan still reports that its content cannot fit.
  const availableWidth = Math.max(
    page.width * 0.05,
    mapFrame.width * page.width - DEFAULT_LEGEND_INSET_MM * 2,
  );
  const availableHeight = Math.max(
    page.height * 0.001,
    mapFrame.height * page.height - DEFAULT_LEGEND_INSET_MM * 2,
  );
  const legacyWidthMm = Math.min(
    availableWidth,
    Math.max(
      28,
      horizontalInset +
        bodyMm * (2.15 * ((legend.symbolSizePercent ?? 100) / 100) + 0.45 + entryLongest * 0.62),
      horizontalInset + fullWidthLongestMm * 0.62,
    ),
  );
  const count = legend.columnCount ?? 1;
  const preferredColumnMm = Math.min(46, Math.max(28, legacyWidthMm - horizontalInset));
  const widthMm =
    count === 1
      ? legacyWidthMm
      : Math.min(
          availableWidth,
          horizontalInset + preferredColumnMm * count + LEGEND_COLUMN_GUTTER_MM * (count - 1),
        );
  const contentHeight = buildLegendLayout(
    legend,
    layers,
    widthMm,
    availableHeight,
    measure,
  ).requiredHeight;
  const heightMm = Math.min(availableHeight, Math.max(1, contentHeight));
  const rightMm =
    (anchor?.right ?? mapFrame.x + mapFrame.width) * page.width -
    (anchor ? 0 : DEFAULT_LEGEND_INSET_MM);
  const bottomMm =
    (anchor?.bottom ?? mapFrame.y + mapFrame.height) * page.height -
    (anchor ? 0 : DEFAULT_LEGEND_INSET_MM);
  return {
    x: Math.max(0, Math.min(1 - widthMm / page.width, (rightMm - widthMm) / page.width)),
    y: Math.max(
      0,
      Math.min(1 - heightMm / page.height, anchor?.top ?? (bottomMm - heightMm) / page.height),
    ),
    width: widthMm / page.width,
    height: heightMm / page.height,
  };
}

/** Conservative semantic fit at an authored physical width, shared by template presets. */
export function estimatedLegendHeightMm(
  legend: CompositionLegend,
  layers: readonly CompositionLegendLayer[],
  widthMm: number,
  measure?: LegendTextMeasurement,
): number {
  return buildLegendLayout(legend, layers, widthMm, Infinity, measure).requiredHeight;
}

export function legendBoundsEqual(left: NormalizedRect, right: NormalizedRect): boolean {
  return (['x', 'y', 'width', 'height'] as const).every(
    (key) => Math.abs(left[key] - right[key]) < 1e-9,
  );
}
